// src/plant.ts
// Slow-growing pixel plants. Watering (once a day, whenever you visit) adds
// growth. They never wilt: if you're away, the plant simply waits for you.
// Fully grown plants go on your shelf and the next species starts as a seed.
//
// Each plant is drawn on a 40x48 grid (taller when it sits on its shelf):
// a pot whose rim is at row 39, and leaves drawn above it. Palette keys
// follow avatars.ts; "." is transparent.

type Sprite = string[];
type Key = string;

const PALETTE: Record<string, string> = {
  o: "#2f3b26", // outline (deep green-brown)
  q: "#2f3b26", // leaf outline (some plants soften it)
  g: "#58b368", // leaf
  G: "#2f7d4f", // leaf shade / stem
  l: "#9be0a6", // leaf highlight / vein
  e: "#f59ab0", // accent on leaves (variegation, edges)
  r: "#e0405a", // flower
  R: "#ff8fa3", // flower highlight
  d: "#5a3a22", // soil
  t: "#d9774b", // seed
  y: "#ffd23f", // gold
  k: "#f59ab0", // butterfly
  K: "#c94f71",
  w: "#ffffff",
  m: "#8a5c30", // pole / trunk
  M: "#6b4423", // trunk shade
  c: "#d9774b", // pot
  C: "#a8532e", // pot shade
  a: "#ffffff", // pot accent
  n: "#5b3a1e", // shelf outline
  h: "#e2a86a", // shelf top edge
  j: "#c4884f", // shelf wood
  J: "#9a6534", // shelf wood shade / grain
};

// ---------- the shelf ----------

// A little wooden shelf the pot sits on: a plank with two brackets.
const SHELF_W = 30;
const SHELF: Sprite = (() => {
  const row = (inner: string) => `n${inner}n`;
  const fill = (key: string) => key.repeat(SHELF_W - 2);
  const grain = (at: number[]) =>
    row(fill("j").split("").map((c, i) => (at.includes(i) ? "J" : c)).join(""));
  const brackets = (left: string, gap: number) => {
    const side = (SHELF_W - gap - left.length * 2) / 2;
    return ".".repeat(side) + left + ".".repeat(gap) + left + ".".repeat(side);
  };
  return [
    "n".repeat(SHELF_W),
    row(fill("h")),
    grain([7, 8, 22]),
    grain([13, 14, 25]),
    row(fill("J")),
    "n".repeat(SHELF_W),
    brackets("nJJJJn", 12),
    brackets("nJJn", 14),
    brackets("nn", 16),
  ];
})();
export const SHELF_ROWS = SHELF.length;

// ---------- pots ----------

// A pot is 8 rows: outline, soil, rim, four body rows, bottom. `widths` is
// each row's full width (centered); `body` colors the inside of the pot.
type PotStyle = { widths: number[]; body: (x: number, row: number) => Key };

const hash = (x: number, y: number, seed = 0) =>
  (((x * 73856093) ^ (y * 19349663) ^ (seed * 83492791)) >>> 0) % 997;

const POTS: Record<string, PotStyle> = {
  terracotta: {
    widths: [16, 16, 16, 14, 14, 12, 12, 10],
    body: (x, row) => (row === 4 && x === 5 ? "C" : "c"),
  },
  ceramic: {
    widths: [12, 14, 16, 16, 16, 16, 14, 12],
    body: (x, row) => (x <= 2 && row <= 4 && row >= 3 ? "a" : x >= 12 && row >= 5 ? "C" : "c"),
  },
  polka: {
    widths: [16, 16, 16, 14, 14, 12, 12, 10],
    body: (x, row) => ((x + row * 2) % 5 === 0 && row % 2 === 1 ? "a" : "c"),
  },
  concrete: {
    widths: [16, 16, 16, 16, 16, 16, 16, 16],
    body: (x, row) => (hash(x, row, 3) % 6 === 0 ? "C" : row === 6 ? "C" : "c"),
  },
  basket: {
    widths: [16, 16, 16, 16, 14, 14, 14, 12],
    body: (x, row) => ((x + row) % 2 === 0 ? "c" : "C"),
  },
  stripes: {
    widths: [14, 14, 14, 12, 12, 12, 10, 10],
    body: (_x, row) => (row % 2 === 0 ? "a" : "c"),
  },
  zigzag: {
    widths: [16, 16, 16, 14, 14, 12, 12, 10],
    body: (x, row) => (row === 4 && x % 2 === 0 ? "a" : row === 5 && x % 2 === 1 ? "a" : "c"),
  },
  bowl: {
    widths: [20, 20, 20, 18, 16, 14, 12, 10],
    body: (_x, row) => (row === 5 ? "a" : row === 6 ? "C" : "c"),
  },
  footed: {
    widths: [16, 16, 16, 14, 12, 8, 8, 12],
    body: (x, row) => (x === 2 && row === 3 ? "a" : row >= 5 ? "C" : "c"),
  },
  starry: {
    widths: [14, 16, 16, 16, 14, 14, 12, 10],
    body: (x, row) => (hash(x, row, 9) % 7 === 0 ? "y" : row === 6 ? "C" : "c"),
  },
};

// The pot as a sprite. A "fancy" pot (a watering bonus) gets a gold rim.
function potSprite(style: PotStyle, fancy: boolean): Sprite {
  const max = Math.max(...style.widths);
  return style.widths.map((w, row) => {
    const pad = ".".repeat((max - w) / 2);
    let inner: string;
    if (row === 0 || row === 7) inner = "o".repeat(w - 2);
    else if (row === 1) inner = "d".repeat(w - 2);
    else if (row === 2) inner = (fancy ? "y" : "C").repeat(w - 2);
    else inner = Array.from({ length: w - 2 }, (_, x) => style.body(x, row)).join("");
    return pad + "o" + inner + "o" + pad;
  });
}

// ---------- species ----------

type Species = {
  name: string;
  colors: Record<string, string>; // palette overrides (leaves, pot, accents)
  pot: keyof typeof POTS;
  grow: (stage: number, p: Pen, layer: "back" | "front") => void;
  stageNames?: Partial<Record<number, string>>;
};

export const KINDS: Record<string, Species> = {};

// The order plants are grown in. ("albo" was an earlier recolored monstera;
// it isn't grown anymore but still shows on shelves that have one.)
export const KIND_ORDER = [
  "monstera",
  "pinkPrincess",
  "goldenPothos",
  "snakePlant",
  "fiddleLeaf",
  "stringOfPearls",
  "cactus",
  "fern",
  "anthurium",
  "moonlight",
];

export const nextKind = (kind: string) =>
  KIND_ORDER[(KIND_ORDER.indexOf(kind) + 1) % KIND_ORDER.length];

// Fully grown at this many waterings (it can keep going for bonuses).
export const FINISHED_AT = 60;

// Waterings needed for each stage (about 2 months to full size).
export const STAGE_AT = [0, 1, 4, 9, 16, 25, 36, 48, 60];
const STAGE_NAMES = [
  "a seed, waiting",
  "a tiny sprout",
  "its very first leaf",
  "a couple of leaves",
  "a leafy little plant",
  "growing fast",
  "nearly grown",
  "a big, happy {name}",
  "a magnificent {name}",
];
export const BONUSES = [
  { at: 75, label: "a butterfly visitor" },
  { at: 90, label: "a gold-trimmed pot" },
  { at: 120, label: "a little sparkle" },
];

export function stageFor(waterings: number): number {
  let s = 0;
  STAGE_AT.forEach((at, i) => {
    if (waterings >= at) s = i;
  });
  return s;
}

// What this plant is up to, e.g. "climbing a moss pole".
export function stageName(kind: string, waterings: number): string {
  const sp = KINDS[kind] || KINDS.monstera;
  const s = stageFor(waterings);
  return (sp.stageNames?.[s] || STAGE_NAMES[s]).replace("{name}", sp.name);
}

// Waterings until the next visible change (stage or bonus), or null.
export function untilNext(waterings: number): number | null {
  const next = [...STAGE_AT, ...BONUSES.map((b) => b.at)]
    .filter((at) => at > waterings)
    .sort((a, b) => a - b)[0];
  return next === undefined ? null : next - waterings;
}

// ---------- drawing tools ----------

type LeafShape = "ellipse" | "heart" | "drop" | "sword" | "fiddle";
type LeafOpts = {
  shape: LeafShape;
  fill?: Key;
  vein?: Key; // midrib color (bigger leaves)
  outline?: Key;
  // per-pixel override: variegation, bands, shimmer...
  paint?: (x: number, y: number, u: number, v: number, len: number, wid: number) => Key | null;
};

type Pen = {
  set: (x: number, y: number, k: Key) => void;
  line: (x0: number, y0: number, x1: number, y1: number, k: Key) => void;
  stamp: (sprite: Sprite, cx: number, bottom: number) => void;
  // A leaf growing from (bx, by) toward `angle` degrees (0 = right, -90 = up),
  // `len` long and `wid` wide (both half-sizes).
  leaf: (bx: number, by: number, angle: number, len: number, wid: number, o: LeafOpts) => void;
  // A filled, outlined region (anything `inside` says yes to).
  blob: (
    inside: (x: number, y: number) => boolean,
    box: [number, number, number, number],
    fill: (x: number, y: number) => Key,
    outline?: Key
  ) => Set<number>;
  soil: number; // the pot rim row; stems start here
};

const SPROUT: Sprite = ["gg.gg", "qglgq", ".qgq.", "..G.."];

const BUTTERFLY: Sprite = ["kk.kk", "kKoKk", ".kok.", "k.o.k"];
const SPARKLE: Sprite = [".y.", "yyy", ".y."];

const rad = (deg: number) => (deg * Math.PI) / 180;

// One number per pixel, safe for pixels a little outside the grid.
const cellKey = (x: number, y: number) => (y + 100) * 1000 + (x + 100);

// ---------- the ten plants ----------

// 1. Monstera: split leaves climbing a moss pole (the original plant).
const YOUNG_LEAF: Sprite = [
  "..qqqqq..",
  ".qgggggq.",
  "qgglglggq",
  "qggglgggq",
  "qggglgggq",
  ".qgglggq.",
  "..qglgq..",
  "...qGq...",
];
const MID_LEAF: Sprite = [
  "...qqqqq...",
  "..qggggggq.",
  ".qgg.glgggq",
  "qggggglgg.q",
  "qg.gglggggq",
  "qggggglgg.q",
  ".qgg.glgggq",
  "..qggglggq.",
  "...qglgq...",
  "....qGq....",
];
const BIG_LEAF: Sprite = [
  "....qqqqq....",
  "..qqgggggqq..",
  ".qg.gglgg.gq.",
  "qggggglggggq.",
  "qg.g.glg.g.gq",
  "qggggglgggggq",
  "q.gg.glg.gg.q",
  "qgggg.l.ggggq",
  ".qgg.glg.ggq.",
  "..qggglgggq..",
  "...qq.l.qq...",
  ".....qGq.....",
];
type MLeaf = { kind: "young" | "mid" | "big"; x: number; y: number };
const MONSTERA_STAGES: { leaves: MLeaf[]; pole?: boolean }[] = [
  { leaves: [] },
  { leaves: [] },
  { leaves: [{ kind: "young", x: 20, y: 30 }] },
  { leaves: [{ kind: "young", x: 16, y: 30 }, { kind: "young", x: 24, y: 27 }] },
  {
    leaves: [
      { kind: "young", x: 14, y: 31 },
      { kind: "mid", x: 22, y: 24 },
      { kind: "young", x: 27, y: 32 },
    ],
  },
  {
    leaves: [
      { kind: "mid", x: 13, y: 27 },
      { kind: "mid", x: 26, y: 26 },
      { kind: "young", x: 20, y: 20 },
    ],
  },
  {
    pole: true,
    leaves: [
      { kind: "big", x: 12, y: 27 },
      { kind: "mid", x: 27, y: 28 },
      { kind: "mid", x: 20, y: 16 },
    ],
  },
  {
    pole: true,
    leaves: [
      { kind: "big", x: 11, y: 25 },
      { kind: "big", x: 29, y: 26 },
      { kind: "mid", x: 20, y: 13 },
      { kind: "young", x: 15, y: 34 },
    ],
  },
  {
    pole: true,
    leaves: [
      { kind: "big", x: 10, y: 24 },
      { kind: "big", x: 30, y: 25 },
      { kind: "big", x: 20, y: 13 },
      { kind: "mid", x: 13, y: 35 },
      { kind: "mid", x: 27, y: 35 },
    ],
  },
];
const MSPRITES = { young: YOUNG_LEAF, mid: MID_LEAF, big: BIG_LEAF };

function growMonstera(s: number, p: Pen, layer: "back" | "front") {
  if (layer !== "back") return;
  const st = MONSTERA_STAGES[s];
  if (st.pole) for (let y = 12; y <= p.soil; y++) (p.set(19, y, "m"), p.set(20, y, "m"));
  st.leaves.forEach((l) => p.line(20, p.soil, l.x, l.y, "G"));
  const order = ["big", "mid", "young"];
  [...st.leaves]
    .sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind))
    .forEach((l) => p.stamp(MSPRITES[l.kind], l.x, l.y));
}

KINDS.monstera = {
  name: "Monstera",
  colors: {},
  pot: "terracotta",
  grow: growMonstera,
  stageNames: { 5: "its first split leaves", 6: "climbing a moss pole" },
};

// Older shelves may have this one.
KINDS.albo = {
  name: "Albo Monstera",
  colors: { g: "#6cbf7a", l: "#ffffff" },
  pot: "terracotta",
  grow: growMonstera,
  stageNames: { 5: "its first split leaves", 6: "climbing a moss pole" },
};

// 2. Pink Princess philodendron: dark heart leaves splashed with pink,
// climbing a little stake.
KINDS.pinkPrincess = {
  name: "Pink Princess",
  colors: {
    g: "#2f6b45", G: "#7a2e4a", l: "#4e9466", e: "#f59ab0", m: "#c9a46a",
    c: "#f4f1ea", C: "#cfc8bb", a: "#ffffff",
  },
  pot: "ceramic",
  stageNames: { 5: "climbing its little stake", 8: "a magnificent Pink Princess" },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 2) return;
    const count = s - 1;
    const top = p.soil - (7 + (s - 2) * 3.6);
    if (s >= 5) for (let y = Math.round(top) - 2; y <= p.soil; y++) p.set(21, y, "m");
    p.line(20, p.soil, 20, Math.round(top), "G");
    const size = 3 + s * 0.35;
    for (let i = 0; i < count; i++) {
      const y = Math.round(p.soil - 5 - (i * (p.soil - 5 - top)) / Math.max(1, count - 1));
      const side = i % 2 === 0 ? -1 : 1;
      const len = i === count - 1 && count > 1 ? size * 0.75 : size;
      const ang = side > 0 ? 40 - (i === count - 1 ? 60 : 0) : 140 + (i === count - 1 ? 60 : 0);
      p.line(20, y, 20 + side * 2, y, "G");
      p.leaf(20 + side * 2, y, ang, len, len * 0.85, {
        shape: "heart",
        vein: s >= 4 ? "l" : undefined,
        paint: (x, yy, u, v) =>
          (i % 3 === 1 && v < -0.5) || hash(x, yy, i) % 9 === 0 ? "e" : null,
      });
    }
  },
};

// 3. Golden Pothos: gold-streaked heart leaves, with vines that trail over
// the front of the pot and down past the shelf.
KINDS.goldenPothos = {
  name: "Golden Pothos",
  colors: {
    g: "#8cc63f", G: "#4f7f24", l: "#c8e67a", e: "#f3dd6a",
    c: "#4fa3a3", C: "#357a7a", a: "#ffffff",
  },
  pot: "polka",
  stageNames: { 4: "its first trailing vine", 6: "vines spilling over the pot" },
  grow: (s, p, layer) => {
    if (s < 2) return;
    const streak = (x: number, y: number, u: number, v: number) =>
      hash(x, y, 7) % 5 === 0 || (Math.abs(v - u * 0.4) < 0.6 && u > 0) ? "e" : null;
    if (layer === "back") {
      const angles = [-100, -60, -135, -35, -80, -120];
      const n = Math.min(s - 1, 6);
      for (let i = 0; i < n; i++) {
        const len = Math.min(2.5 + s * 0.4, 5.2) * (i === 0 ? 1 : 0.9);
        const a = angles[i];
        const stem = 3 + (i % 2) * 2;
        const bx = 20 + Math.cos(rad(a)) * stem;
        const by = p.soil + Math.sin(rad(a)) * stem;
        p.line(20, p.soil, Math.round(bx), Math.round(by), "G");
        p.leaf(bx, by, a, len, len * 0.8, { shape: "heart", vein: "l", paint: streak });
      }
      return;
    }
    // front: trailing vines over the pot
    // vines start at the pot's edges, so the pot stays in view
    const vines = s >= 4 ? (s >= 6 ? 3 : 2) : 0;
    const starts = [12, 28, 13];
    const drift = [-1, 1, -1];
    for (let vi = 0; vi < vines; vi++) {
      const length = (s - 3) * 3 + vi * 2;
      for (let k = 0; k <= length; k++) {
        const y = p.soil + k;
        const x = starts[vi] + Math.round(Math.sin(k / 2.5 + vi) * 1.2) + drift[vi] * Math.floor(k / 5);
        p.set(x, y, "G");
        if (k % 3 === 2) {
          const side = (k / 3) % 2 < 1 ? -1 : 1;
          p.leaf(x + side, y, side > 0 ? 50 : 130, 2.2, 1.8, { shape: "heart", paint: streak });
        }
      }
    }
  },
};

// 4. Snake Plant: tall, striped sword leaves with golden edges.
KINDS.snakePlant = {
  name: "Snake Plant",
  colors: {
    g: "#3e7d4c", G: "#2c5a37", l: "#86c08a", e: "#e3d26a",
    c: "#b9bcc0", C: "#8d9196",
  },
  pot: "concrete",
  stageNames: { 1: "a tiny spike", 2: "its first spiky leaf", 3: "two spiky leaves" },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 1) return;
    const offsets = [0, -3, 3, -5, 5, -1, 2, -6];
    const maxH = [0, 2, 4, 5.5, 7, 8.5, 10, 11.5, 13][s];
    const wid = s < 4 ? 1.4 : 2;
    const order = Array.from({ length: Math.min(s, 8) }, (_, i) => i).reverse();
    order.forEach((i) => {
      const dx = offsets[i];
      const h = maxH * (1 - (i % 3) * 0.13);
      p.leaf(20 + dx, p.soil, -90 + dx * 2.4, h, wid, {
        shape: "sword",
        paint: (x, y, u, v, len, w) =>
          Math.abs(v) > w - 0.9 && s >= 3 ? "e" : (y + i) % 4 === 0 ? "l" : null,
      });
    });
  },
};

// 5. Fiddle Leaf Fig: a little tree with big violin-shaped leaves.
KINDS.fiddleLeaf = {
  name: "Fiddle Leaf Fig",
  colors: {
    g: "#4f8f3a", G: "#3a6a2a", l: "#a5d36d", m: "#8a5c30", M: "#5e3d1f",
    c: "#d8b27a", C: "#a87f45",
  },
  pot: "basket",
  stageNames: { 4: "a little trunk", 6: "a tiny tree" },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 2) return;
    const H = [0, 0, 5, 8, 11, 14, 17, 20, 23][s];
    const top = p.soil - H;
    for (let y = top; y <= p.soil; y++) {
      p.set(20, y, "m");
      if (s >= 5) p.set(21, y, "M");
    }
    const n = [0, 0, 2, 3, 4, 5, 6, 7, 8][s];
    const size = [0, 0, 2.6, 3, 3.5, 4, 4.5, 5, 5.5][s];
    // lower leaves first so the top of the tree sits in front
    for (let i = n - 1; i >= 0; i--) {
      const y = top + 1 + i * 2;
      const side = i === 0 ? 0 : i % 2 === 1 ? 1 : -1;
      const ang = side === 0 ? -90 : side > 0 ? -30 - (i % 3) * 8 : -150 + (i % 3) * 8;
      const len = size * (i === 0 ? 0.9 : 1);
      p.leaf(20 + side, y, ang, len, len * 0.72, {
        shape: "fiddle",
        vein: "l",
        paint: (x, yy, u, v) => (len > 4 && Math.abs(Math.abs(v) - (u + len) * 0.35) < 0.45 && u > -len * 0.4 ? "l" : null),
      });
    }
  },
};

// 6. String of Pearls: beaded strands that spill over the pot and shelf.
KINDS.stringOfPearls = {
  name: "String of Pearls",
  colors: {
    g: "#7fc96b", G: "#3f7f3a", l: "#d6f5c2",
    c: "#f5a9c4", C: "#d07a9b", a: "#ffe3ee",
  },
  pot: "stripes",
  stageNames: {
    2: "a few little pearls",
    4: "its first string of pearls",
    6: "strings spilling over the pot",
  },
  grow: (s, p, layer) => {
    const pearl = (x: number, y: number) => {
      p.set(x, y, "l");
      p.set(x + 1, y, "g");
      p.set(x, y + 1, "g");
      p.set(x + 1, y + 1, "G");
    };
    if (s < 1) return;
    if (layer === "back") {
      // a dome of pearls on the soil, rising as it grows
      const rows = Math.min(1 + Math.floor(s / 2), 5);
      for (let r = rows - 1; r >= 0; r--) {
        const hw = Math.max(1, 6 - r * 1.4);
        for (let x = Math.round(20 - hw) + (r % 2); x <= 20 + hw; x += 2) pearl(x - 1, p.soil - 2 - r * 2);
      }
      return;
    }
    // front: strands spilling over the sides of the pot, down past the shelf
    const strands = Math.max(0, Math.min(s - 2, 4));
    const xs = [11, 27, 13, 25];
    for (let i = 0; i < strands; i++) {
      const length = Math.round((s - 2) * 2.4 + (i % 2) * 2);
      const out = i < 2 ? (i === 0 ? -1 : 1) : 0;
      for (let k = 0; k <= length; k += 2) {
        const x = xs[i] + Math.round(Math.sin((k + i * 3) / 3) * 0.8) + out * Math.floor(k / 6);
        pearl(x, p.soil - 1 + k);
      }
    }
  },
};

// 7. Cactus: a ribbed column with spines; grows arms, then a pink flower.
KINDS.cactus = {
  name: "Cactus",
  colors: {
    g: "#5fae5a", G: "#3d7e3a", l: "#8fd389", w: "#fff8e1", r: "#ff6fa3", R: "#ffd1e3",
    c: "#e9a36b", C: "#b86f3d", a: "#fff1d6",
  },
  pot: "zigzag",
  stageNames: {
    1: "a tiny green nub",
    2: "a little cactus",
    5: "its first arm",
    7: "a flower bud!",
    8: "a magnificent cactus in bloom",
  },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 1) return;
    const H = [0, 2, 4, 6, 9, 12, 15, 17, 19][s];
    const hw = s < 3 ? 1 : s < 5 ? 2 : 3;
    const top = p.soil - H;
    const armL = s >= 5 ? { y: Math.round(p.soil - H * 0.45), up: Math.min(3 + (s - 5) * 2, 7) } : null;
    const armR = s >= 6 ? { y: Math.round(p.soil - H * 0.62), up: Math.min(3 + (s - 6) * 2, 5) } : null;
    const inside = (x: number, y: number) => {
      if (y > p.soil) return false;
      // body with a rounded top
      if (Math.abs(x - 20) <= hw && y >= top + 1) return true;
      if (y === top && Math.abs(x - 20) <= hw - 1) return true;
      // arms: out to the side, then up
      // (3 pixels thick, so there's green inside the outline)
      if (armL) {
        const ax = 20 - hw - 4;
        if (y >= armL.y - 2 && y <= armL.y && x >= ax && x < 20 - hw) return true;
        if (x >= ax && x <= ax + 2 && y >= armL.y - armL.up && y <= armL.y)
          return y > armL.y - armL.up || x === ax + 1;
      }
      if (armR) {
        const ax = 20 + hw + 4;
        if (y >= armR.y - 2 && y <= armR.y && x <= ax && x > 20 + hw) return true;
        if (x <= ax && x >= ax - 2 && y >= armR.y - armR.up && y <= armR.y)
          return y > armR.y - armR.up || x === ax - 1;
      }
      return false;
    };
    const cells = p.blob(inside, [8, top - 2, 32, p.soil], (x) => ((x - 20) % 2 === 0 ? "G" : x === 19 || x === 21 ? "l" : "g"), "q");
    // little spines poking out of the outline
    for (let y = top - 1; y <= p.soil; y++)
      for (let x = 8; x <= 32; x++)
        if (
          !cells.has(cellKey(x, y)) &&
          hash(x, y, 4) % 6 === 0 &&
          (cells.has(cellKey(x - 1, y)) || cells.has(cellKey(x + 1, y)))
        )
          p.set(x, y, "w");
    if (s === 7) (p.set(20, top - 1, "r"), p.set(19, top - 1, "R"));
    if (s >= 8) p.stamp([".r.r.", "rRrRr", ".ryr.", "..r.."], 20, top);
  },
};

// 8. Boston Fern: arching, fluffy fronds.
KINDS.fern = {
  name: "Boston Fern",
  colors: { g: "#6fbf4a", G: "#3f7f2a", l: "#a6e07a", c: "#6aa8e0", C: "#3f78b0", a: "#cfe6fb" },
  pot: "bowl",
  stageNames: { 4: "unfurling fronds", 6: "a fluffy fern" },
  grow: (s, p, layer) => {
    // drawn in front, so the outer fronds can cascade over the bowl's rim
    if (layer !== "front" || s < 1) return;
    const n = [0, 1, 2, 3, 5, 7, 9, 11, 13][s];
    const L = [0, 3, 5, 7, 10, 12, 14, 16, 17][s];
    // outer fronds first, so the upright middle ones sit in front
    const order = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => Math.abs(b - (n - 1) / 2) - Math.abs(a - (n - 1) / 2)
    );
    for (const i of order) {
      const t0 = n === 1 ? 0.5 : i / (n - 1);
      const a = rad(-170 + t0 * 160);
      const len = L * (0.8 + 0.2 * Math.sin(Math.PI * t0));
      const sag = 0.2 + 0.85 * Math.abs(Math.cos(a)); // sideways fronds droop more
      const steps = Math.ceil(len);
      for (let k = 0; k <= steps; k++) {
        const t = k / steps;
        const x = 20 + Math.cos(a) * len * t;
        const y = p.soil - 1 + Math.sin(a) * len * t + t * t * len * sag; // arches, then droops
        const px = Math.round(x);
        const py = Math.round(y);
        p.set(px, py, "G");
        // leaflets on both sides, shorter toward the tip
        const leaflet = Math.max(0, Math.round((1 - t) * 2.2));
        const nx = -Math.sin(a);
        const ny = Math.cos(a);
        for (let j = 1; j <= leaflet; j++) {
          p.set(Math.round(x + nx * j), Math.round(y + ny * j), j === 1 && k % 2 ? "l" : "g");
          p.set(Math.round(x - nx * j), Math.round(y - ny * j), j === 1 && k % 2 === 0 ? "l" : "g");
        }
      }
    }
  },
};

// 9. Anthurium: dark, glossy heart leaves and red heart-shaped blooms.
KINDS.anthurium = {
  name: "Anthurium",
  colors: {
    g: "#2f7d4f", G: "#24603c", l: "#6fbf7f", r: "#e0405a", R: "#ff8fa3", y: "#ffd23f",
    c: "#3a3a44", C: "#24242c", a: "#8a8a99",
  },
  pot: "footed",
  stageNames: { 5: "its first heart-shaped bloom", 7: "covered in blooms" },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 2) return;
    const leaves = [0, 0, 1, 2, 2, 3, 3, 4, 5][s];
    const size = [0, 0, 3, 3.4, 3.8, 4.2, 4.6, 5, 5.4][s];
    const spots = [
      { dx: -7, h: 10, ang: 120 },
      { dx: 7, h: 11, ang: 60 },
      { dx: -2, h: 15, ang: 100 },
      { dx: 4, h: 7, ang: 70 },
      { dx: -9, h: 4, ang: 150 },
    ];
    for (let i = 0; i < leaves; i++) {
      const sp = spots[i];
      const h = Math.min(sp.h, 4 + s * 1.6);
      const bx = 20 + sp.dx;
      const by = p.soil - h;
      p.line(20, p.soil, bx, by, "G");
      p.leaf(bx, by, sp.ang, size, size * 0.85, { shape: "heart", vein: "l" });
    }
    const blooms = s >= 8 ? 3 : s >= 7 ? 2 : s >= 5 ? 1 : 0;
    const fl = [
      { dx: 4, h: 19 },
      { dx: -5, h: 17 },
      { dx: 10, h: 13 },
    ];
    for (let i = 0; i < blooms; i++) {
      const f = fl[i];
      const bx = 20 + f.dx;
      const by = p.soil - f.h;
      p.line(20, p.soil, bx, by, "G");
      const tilt = -90 + f.dx * 3;
      p.leaf(bx, by, tilt, 3.6, 3.3, {
        shape: "heart",
        fill: "r",
        paint: (x, y, u, v) => (v < -1 && u < 1 ? "R" : null),
      });
      // the little yellow spike, sticking out of the middle of the bloom
      const sx = bx + Math.cos(rad(tilt)) * 2.5;
      const sy = by + Math.sin(rad(tilt)) * 2.5;
      p.line(sx, sy, sx + (f.dx >= 0 ? 2 : -2), sy - 2, "y");
    }
  },
};

// 10. Moonlight: silvery, shimmering leaves on short stems.
KINDS.moonlight = {
  name: "Moonlight",
  colors: {
    g: "#bfe8d0", G: "#5aa07a", l: "#ffffff", q: "#4d7a66",
    c: "#c7b3e6", C: "#9a82c4",
  },
  pot: "starry",
  stageNames: { 5: "shimmering silver leaves", 8: "a magnificent, moonlit Moonlight" },
  grow: (s, p, layer) => {
    if (layer !== "back" || s < 2) return;
    const n = [0, 0, 2, 3, 4, 5, 6, 7, 8][s];
    const size = [0, 0, 2.8, 3.2, 3.6, 4, 4.4, 4.8, 5.2][s];
    for (let i = 0; i < n; i++) {
      const t = n === 1 ? 0.5 : i / (n - 1);
      const a = -150 + t * 120;
      const stem = 2 + ((i * 3) % 4) + s * 0.6;
      const bx = 20 + Math.cos(rad(a)) * stem;
      const by = p.soil + Math.sin(rad(a)) * stem;
      p.line(20, p.soil, Math.round(bx), Math.round(by), "G");
      p.leaf(bx, by, a, size, size * 0.6, {
        shape: "drop",
        paint: (x, y, u, v) => (hash(x, y, 2) % 4 === 0 || Math.abs(v) < 0.5 ? "l" : null),
      });
    }
  },
};

// ---------- rendering ----------

export const PLANT_W = 40;
export const PLANT_H = 48;

// Build the grid of colors for a plant with this many waterings.
// With `onShelf`, the grid is SHELF_ROWS taller and a wooden shelf sits under the pot.
export function renderPlant(
  waterings: number,
  kind = "monstera",
  onShelf = false
): (string | null)[][] {
  const sp = KINDS[kind] || KINDS.monstera;
  const palette = { ...PALETTE, ...sp.colors };
  const height = PLANT_H + (onShelf ? SHELF_ROWS : 0);
  const grid: (string | null)[][] = Array.from({ length: height }, () =>
    Array<string | null>(PLANT_W).fill(null)
  );
  const set = (x: number, y: number, key: string) => {
    x = Math.round(x);
    y = Math.round(y);
    if (x >= 0 && x < PLANT_W && y >= 0 && y < height && palette[key]) grid[y][x] = palette[key];
  };
  const stamp = (sprite: Sprite, cx: number, bottom: number) => {
    const w = sprite[0].length;
    const left = cx - Math.floor(w / 2);
    const top = bottom - sprite.length + 1;
    sprite.forEach((row, dy) =>
      row.split("").forEach((key, dx) => key !== "." && set(left + dx, top + dy, key))
    );
  };
  const line = (x0: number, y0: number, x1: number, y1: number, key: string) => {
    const steps = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
    for (let i = 0; i <= steps; i++) {
      set(x0 + ((x1 - x0) * i) / steps, y0 + ((y1 - y0) * i) / steps, key);
    }
  };
  const blob: Pen["blob"] = (inside, [x0, y0, x1, y1], fill, outline = "q") => {
    const cells = new Set<number>(); // keys from cellKey()
    for (let y = Math.floor(y0); y <= Math.ceil(y1); y++)
      for (let x = Math.floor(x0); x <= Math.ceil(x1); x++) if (inside(x, y)) cells.add(cellKey(x, y));
    cells.forEach((c) => {
      const x = (c % 1000) - 100;
      const y = Math.floor(c / 1000) - 100;
      const edge = [cellKey(x - 1, y), cellKey(x + 1, y), cellKey(x, y - 1), cellKey(x, y + 1)].some(
        (n) => !cells.has(n)
      );
      set(x, y, edge ? outline : fill(x, y));
    });
    return cells;
  };
  const leaf: Pen["leaf"] = (bx, by, angle, len, wid, o) => {
    len = Math.max(len, 1.6);
    wid = Math.max(wid, 1.1);
    const a = rad(angle);
    const cx = bx + Math.cos(a) * len;
    const cy = by + Math.sin(a) * len;
    const local = (x: number, y: number) => {
      const dx = x - cx;
      const dy = y - cy;
      return { u: dx * Math.cos(a) + dy * Math.sin(a), v: -dx * Math.sin(a) + dy * Math.cos(a) };
    };
    const inShape = (x: number, y: number) => {
      const { u, v } = local(x, y);
      const t = (u + len) / (2 * len); // 0 at the stem, 1 at the tip
      switch (o.shape) {
        case "ellipse":
          return (u / len) ** 2 + (v / wid) ** 2 <= 1;
        case "drop": {
          const w = wid * (1 - 0.55 * Math.max(0, u / len));
          return (u / len) ** 2 + (v / w) ** 2 <= 1;
        }
        case "heart": {
          const w = wid * (1 - 0.5 * Math.max(0, u / len));
          return (u / len) ** 2 + (v / w) ** 2 <= 1 && !(u < -0.55 * len && Math.abs(v) < 0.3 * wid);
        }
        case "sword": {
          if (t < 0 || t > 1) return false;
          const w = t < 0.72 ? wid : (wid * (1 - t)) / 0.28;
          return Math.abs(v) <= w;
        }
        case "fiddle": {
          if (t < 0 || t > 1) return false;
          const w = wid * Math.sqrt(Math.max(0, Math.sin(Math.PI * t))) * (0.55 + 0.45 * t);
          return Math.abs(v) <= w;
        }
      }
      return false;
    };
    const r = len + wid + 1;
    blob(
      inShape,
      [cx - r, cy - r, cx + r, cy + r],
      (x, y) => {
        const { u, v } = local(x, y);
        const custom = o.paint?.(x, y, u, v, len, wid);
        if (custom) return custom;
        if (o.vein && len >= 3.4 && Math.abs(v) < 0.6 && u > -len * 0.85) return o.vein;
        return o.fill || "g";
      },
      o.outline || "q"
    );
  };

  const pen: Pen = { set, line, stamp, leaf, blob, soil: 39 };
  const stage = stageFor(waterings);

  sp.grow(stage, pen, "back");
  if (stage === 1 && !["snakePlant", "cactus", "fern", "stringOfPearls"].includes(kind)) {
    stamp(SPROUT, 20, 38);
  }
  if (waterings === 0) {
    set(19, 38, "t");
    set(20, 38, "t");
  }

  stamp(potSprite(POTS[sp.pot], waterings >= 90), 20, 46);
  if (onShelf) stamp(SHELF, 20, 46 + SHELF_ROWS);
  sp.grow(stage, pen, "front"); // trailing vines hang in front of the pot

  if (waterings >= 75) stamp(BUTTERFLY, 33, 9);
  if (waterings >= 120) {
    stamp(SPARKLE, 6, 6);
    stamp(SPARKLE, 34, 20);
  }
  return grid;
}

// ---------- watering ----------

export type PlantState = { waterings: number; lastWatered: string; kind: string };
export type ShelfPlant = { kind: string; waterings: number; finishedOn: string };

export const MAX_SHELF = 40;

export const todayKey = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local

const validKind = (k: unknown) => (typeof k === "string" && k in KINDS ? k : "monstera");

export function normalizePlant(raw: any): PlantState {
  return {
    waterings: Number.isInteger(raw?.waterings) && raw.waterings > 0 ? raw.waterings : 0,
    lastWatered: typeof raw?.lastWatered === "string" ? raw.lastWatered : "",
    kind: validKind(raw?.kind),
  };
}

export function normalizeShelf(raw: any): ShelfPlant[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, MAX_SHELF).map((p) => ({
    kind: validKind(p?.kind),
    waterings: Number.isInteger(p?.waterings) ? p.waterings : FINISHED_AT,
    finishedOn: typeof p?.finishedOn === "string" ? p.finishedOn : "",
  }));
}
